import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import theme from '@styles/theme';

type LoaderProps = {
	disabled?: boolean;
	isBlack?: boolean;
	height?: number;
};

const Loader = ({ disabled, isBlack = false, height }: LoaderProps) => {
	return (
		<Wrapper $disabled={disabled} $height={height}>
	        <LoaderBox $disabled={disabled} $isBlack={isBlack} aria-hidden />
	    </Wrapper>
	);
};

export default Loader;

const mulShdSpin = keyframes`
  0%, 100% {
    box-shadow: 0em -2.6em 0em 0em currentColor,
      1.8em -1.8em 0 0em var(--s2),
    2.5em 0em 0 0em var(--s2),
    1.75em 1.75em 0 0em var(--s2),
    0em 2.5em 0 0em var(--s2),
    -1.8em 1.8em 0 0em var(--s2),
    -2.6em 0em 0 0em var(--s3),
      -1.8em -1.8em 0 0em var(--s4);
  }

  12.5% {
    box-shadow: 0em -2.6em 0em 0em var(--s4),
      1.8em -1.8em 0 0em currentColor,
    2.5em 0em 0 0em var(--s2),
    1.75em 1.75em 0 0em var(--s2),
    0em 2.5em 0 0em var(--s2),
    -1.8em 1.8em 0 0em var(--s2),
    -2.6em 0em 0 0em var(--s2),
      -1.8em -1.8em 0 0em var(--s3);
  }

  25% {
    box-shadow: 0em -2.6em 0em 0em var(--s3),
      1.8em -1.8em 0 0em var(--s4),
    2.5em 0em 0 0em currentColor,
    1.75em 1.75em 0 0em var(--s2),
    0em 2.5em 0 0em var(--s2),
    -1.8em 1.8em 0 0em var(--s2),
    -2.6em 0em 0 0em var(--s2),
      -1.8em -1.8em 0 0em var(--s2);
  }

  37.5% {
    box-shadow: 0em -2.6em 0em 0em var(--s2),
      1.8em -1.8em 0 0em var(--s3),
    2.5em 0em 0 0em var(--s4),
    1.75em 1.75em 0 0em currentColor,
    0em 2.5em 0 0em var(--s2),
    -1.8em 1.8em 0 0em var(--s2),
    -2.6em 0em 0 0em var(--s2),
      -1.8em -1.8em 0 0em var(--s2);
  }

  50% {
    box-shadow: 0em -2.6em 0em 0em var(--s2),
      1.8em -1.8em 0 0em var(--s2),
    2.5em 0em 0 0em var(--s3),
    1.75em 1.75em 0 0em var(--s4),
    0em 2.5em 0 0em currentColor,
    -1.8em 1.8em 0 0em var(--s2),
    -2.6em 0em 0 0em var(--s2),
      -1.8em -1.8em 0 0em var(--s2);
  }

  62.5% {
    box-shadow: 0em -2.6em 0em 0em var(--s2),
      1.8em -1.8em 0 0em var(--s2),
    2.5em 0em 0 0em var(--s2),
    1.75em 1.75em 0 0em var(--s3),
    0em 2.5em 0 0em var(--s4),
    -1.8em 1.8em 0 0em currentColor,
    -2.6em 0em 0 0em var(--s2),
      -1.8em -1.8em 0 0em var(--s2);
  }

  75% {
    box-shadow: 0em -2.6em 0em 0em var(--s2),
      1.8em -1.8em 0 0em var(--s2),
    2.5em 0em 0 0em var(--s2),
    1.75em 1.75em 0 0em var(--s2),
    0em 2.5em 0 0em var(--s3),
    -1.8em 1.8em 0 0em var(--s4),
    -2.6em 0em 0 0em currentColor,
      -1.8em -1.8em 0 0em var(--s2);
  }

  87.5% {
    box-shadow: 0em -2.6em 0em 0em var(--s2),
      1.8em -1.8em 0 0em var(--s2),
    2.5em 0em 0 0em var(--s2),
    1.75em 1.75em 0 0em var(--s2),
    0em 2.5em 0 0em var(--s2),
    -1.8em 1.8em 0 0em var(--s3),
    -2.6em 0em 0 0em var(--s4),
      -1.8em -1.8em 0 0em currentColor;
  }
`;

const Wrapper = styled.div<{ $disabled?: boolean; $height?: number }>`
  width: 100%;
  min-height: ${({ $height }) => ($height ? 'auto' : '200px')};
  height: ${({ $height }) => ($height ? `${$height}px` : '100%')};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 30;

  ${({ $disabled }) => $disabled && css`
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    margin: 0;
    background: rgba(0, 0, 0, 0.13);
  `};
`;

const LoaderBox = styled.div<{ $isBlack?: boolean; $disabled?: boolean }>`
  font-size: 10px;
  width: 1em;
  height: 1em;
  border-radius: 50%;
  position: relative;
  text-indent: -9999em;
  transform: translateZ(0);
  color: ${({ $isBlack }) => ($isBlack ? theme.colors.whiteColor : theme.colors.blackColor)};

  // CSS variables
  --s2: ${({ $isBlack }) => ($isBlack ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)')};
  --s3: ${({ $isBlack }) => ($isBlack ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)')};
  --s4: ${({ $isBlack }) => ($isBlack ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)')};

  animation: ${mulShdSpin} 1.1s infinite ease;

  ${({ $isBlack }) => $isBlack && css`
    //font-size: .32em;
    animation-duration: 1.1s;
  `};

  ${({ $disabled }) => $disabled && css`
    filter: grayscale(100%) opacity(0.8);
  `};
`;
